import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { X, Tag, Search, Check } from 'lucide-react';
import { apiService } from '../services/api';

const DeviceTagsManager = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingTag, setEditingTag] = useState(null);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedTag, setSelectedTag] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Fetch device tags - NO auto-refresh
    const { data: tagsData, isLoading: tagsLoading, error: tagsError } = useQuery(
        'device-tags',
        apiService.getDeviceTags,
        {
            refetchOnWindowFocus: false, // Disable auto-refresh
            onError: (error) => {
                console.error('Error loading device tags:', error);
            }
        }
    );

    const tags = Array.isArray(tagsData?.tags) ? tagsData.tags : [];

    // Fetch devices for assignment
    const { data: devices = [], isLoading: devicesLoading } = useQuery(
        'devices',
        apiService.getDevices,
        {
            select: (data) => {
                if (Array.isArray(data?.devices)) return data.devices;
                if (Array.isArray(data)) return data;
                return [];
            }
        }
    );

    // Create tag mutation
    const createTagMutation = useMutation(
        apiService.createDeviceTag,
        {
            onSuccess: () => {
                queryClient.invalidateQueries('device-tags');
                setShowAddForm(false);
                resetForm();
                toast.success(t('deviceTags.createSuccess', 'Tag created successfully'));
            },
            onError: (error) => {
                console.error('Error creating tag:', error);
                const errorMessage = error?.response?.data?.error
                    || error?.message
                    || t('deviceTags.createError');
                toast.error(errorMessage);
            }
        }
    );

    // Update tag mutation
    const updateTagMutation = useMutation(
        ({ tagId, tagData }) => apiService.updateDeviceTag(tagId, tagData),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('device-tags');
                setEditingTag(null);
                resetForm();
                toast.success(t('deviceTags.updateSuccess', 'Tag updated successfully'));
            },
            onError: (error) => {
                console.error('Error updating tag:', error);
                const errorMessage = error?.response?.data?.error
                    || error?.message
                    || t('deviceTags.updateError');
                toast.error(errorMessage);
            }
        }
    );

    // Delete tag mutation
    const deleteTagMutation = useMutation(
        apiService.deleteDeviceTag,
        {
            onSuccess: () => {
                queryClient.invalidateQueries('device-tags');
                toast.success(t('deviceTags.deleteSuccess', 'Tag deleted successfully'));
            },
            onError: (error) => {
                console.error('Error deleting tag:', error);
                const errorMessage = error?.response?.data?.error
                    || error?.message
                    || t('deviceTags.deleteError');
                toast.error(errorMessage);
            }
        }
    );

    // Assign tag to device mutation
    const assignTagMutation = useMutation(
        ({ tagId, deviceId }) => apiService.assignTagToDevice(tagId, deviceId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('device-tags');
                queryClient.invalidateQueries('devices');
                toast.success(t('deviceTags.assignSuccess', 'Tag assigned successfully'));
            },
            onError: (error) => {
                console.error('Error assigning tag:', error);
                const errorMessage = error?.response?.data?.error
                    || error?.message
                    || t('deviceTags.assignError');
                toast.error(errorMessage);
            }
        }
    );

    // Unassign tag from device mutation
    const unassignTagMutation = useMutation(
        ({ tagId, deviceId }) => apiService.unassignTagFromDevice(tagId, deviceId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('device-tags');
                queryClient.invalidateQueries('devices');
                toast.success(t('deviceTags.unassignSuccess', 'Tag removed successfully'));
            },
            onError: (error) => {
                console.error('Error unassigning tag:', error);
                const errorMessage = error?.response?.data?.error
                    || error?.message
                    || t('deviceTags.unassignError');
                toast.error(errorMessage);
            }
        }
    );

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        color: '#3B82F6'
    });

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            color: '#3B82F6'
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error(t('deviceTags.nameRequired'));
            return;
        }

        if (editingTag) {
            updateTagMutation.mutate({
                tagId: editingTag.id,
                tagData: formData
            });
        } else {
            createTagMutation.mutate(formData);
        }
    };

    const handleEdit = (tag) => {
        setEditingTag(tag);
        setFormData({
            name: tag.name,
            description: tag.description || '',
            color: tag.color || '#3B82F6'
        });
        setShowAddForm(true);
    };

    const handleDelete = (tagId) => {
        if (window.confirm(t('deviceTags.deleteConfirm'))) {
            deleteTagMutation.mutate(tagId);
        }
    };

    const handleAssignTag = async () => {
        if (!selectedTag || selectedDevices.length === 0) return;

        try {
            // Assign tags sequentially to avoid race conditions
            for (const deviceId of selectedDevices) {
                await assignTagMutation.mutateAsync({
                    tagId: selectedTag.id,
                    deviceId: deviceId
                });
            }
            // Close modal only after all assignments succeed
            setShowAssignModal(false);
            setSelectedDevices([]);
            setSelectedTag(null);
        } catch (error) {
            // Error already handled by mutation onError
            console.error('Error assigning tags:', error);
        }
    };

    const handleUnassignTag = (tagId, deviceId) => {
        if (window.confirm(t('deviceTags.unassignConfirm'))) {
            unassignTagMutation.mutate({ tagId, deviceId });
        }
    };

    if (tagsLoading) {
        return (
            <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2">{t('common.loading')}</span>
            </div>
        );
    }

    if (tagsError) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="text-red-800">
                    <strong>{t('common.error')}</strong>
                    <p>{t('deviceTags.loadError')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-full overflow-x-hidden">
            {/* Modern Header */}
            <div className="card">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                    <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-200 via-emerald-300 to-teal-300 flex items-center justify-center shadow-lg">
                            <span className="text-2xl">🏷️</span>
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                                {t('deviceTags.title')}
                            </h1>
                            <p className="text-sm sm:text-base text-gray-600 mt-1">{t('deviceTags.subtitle')}</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowAddForm(!showAddForm)}
                        className="btn-primary flex items-center space-x-2 whitespace-nowrap"
                    >
                        <span>+</span>
                        <span>{t('deviceTags.addTag')}</span>
                    </button>
                </div>
            </div>

            <div className="card">

                {/* Add/Edit Form */}
                {showAddForm && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6 m-6">
                        <h2 className="text-lg font-semibold mb-4">
                            {editingTag ? t('deviceTags.editTag') : t('deviceTags.addTag')}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('deviceTags.tagName')} *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder={t('deviceTags.tagNamePlaceholder')}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('deviceTags.tagColor')}
                                    </label>
                                    <input
                                        type="color"
                                        value={formData.color}
                                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                        className="w-full h-10 border border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('deviceTags.tagDescription')}
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    rows="3"
                                    placeholder={t('deviceTags.tagDescriptionPlaceholder')}
                                />
                            </div>
                            <div className="flex space-x-3">
                                <button
                                    type="submit"
                                    disabled={createTagMutation.isLoading || updateTagMutation.isLoading}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50 transition-colors"
                                >
                                    {editingTag ? t('common.update') : t('common.create')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setEditingTag(null);
                                        resetForm();
                                    }}
                                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md transition-colors"
                                >
                                    {t('common.cancel')}
                                </button>
                            </div>
                        </form>
                    </div>
                )
                }

                {/* Tags List */}
                <div className="p-6">
                    {tags.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                            <p>{t('deviceTags.noTags')}</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {(tags || []).map((tag) => (
                                <div key={tag.id} className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <div
                                                className="w-4 h-4 rounded-full"
                                                style={{ backgroundColor: tag.color }}
                                            ></div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900">{tag.name}</h3>
                                                {tag.description && (
                                                    <p className="text-sm text-gray-600">{tag.description}</p>
                                                )}
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {t('deviceTags.deviceCount', { count: tag.device_count || 0 })}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => {
                                                    setSelectedTag(tag);
                                                    setShowAssignModal(true);
                                                }}
                                                className="bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1 rounded-md text-sm"
                                            >
                                                {t('deviceTags.assign')}
                                            </button>
                                            <button
                                                onClick={() => handleEdit(tag)}
                                                className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded-md text-sm"
                                            >
                                                {t('common.edit')}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(tag.id)}
                                                className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded-md text-sm"
                                            >
                                                {t('common.delete')}
                                            </button>
                                        </div>
                                    </div>
                                    {/* Show assigned devices */}
                                    {tag.devices && tag.devices.length > 0 && (
                                        <div className="mt-3">
                                            <p className="text-sm font-medium text-gray-700 mb-2">
                                                {t('deviceTags.assignedDevices')}:
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {(tag.devices || []).map((device) => (
                                                    <div
                                                        key={device.id}
                                                        className="bg-gray-100 px-2 py-1 rounded-md text-sm flex items-center space-x-1"
                                                    >
                                                        <span>{device.name}</span>
                                                        <button
                                                            onClick={() => handleUnassignTag(tag.id, device.id)}
                                                            className="text-red-600 hover:text-red-800"
                                                            title={t('deviceTags.unassign')}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Assign Tag Modal */}
                {
                    showAssignModal && selectedTag && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                            onClick={() => {
                                setShowAssignModal(false);
                                setSearchQuery('');
                            }}
                        >
                            <div
                                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Modal Header */}
                                <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-emerald-50 via-green-50 to-teal-50 rounded-t-2xl flex-shrink-0">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-200 to-emerald-300 flex items-center justify-center shadow-md">
                                                <Tag className="w-6 h-6 text-green-700" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-gray-900">
                                                    {t('deviceTags.assignTagToDevices', 'Assign Tag to Devices')}
                                                </h2>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <div
                                                        className="w-3 h-3 rounded-full shadow-sm"
                                                        style={{ backgroundColor: selectedTag.color }}
                                                    ></div>
                                                    <p className="text-sm text-gray-600 font-medium">
                                                        {selectedTag.name}
                                                    </p>
                                                    {selectedTag.description && (
                                                        <span className="text-xs text-gray-500">• {selectedTag.description}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowAssignModal(false);
                                                setSearchQuery('');
                                            }}
                                            className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                {/* Search Bar */}
                                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder={t('deviceTags.searchDevices', 'Search devices...')}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                        />
                                    </div>
                                    <div className="flex items-center justify-between mt-3">
                                        <p className="text-sm text-gray-600">
                                            {selectedDevices.length > 0 ? (
                                                <span className="font-semibold text-green-600">
                                                    {selectedDevices.length} {t('deviceTags.selected', 'selected')}
                                                </span>
                                            ) : (
                                                t('deviceTags.selectDevices', 'Select devices to assign this tag')
                                            )}
                                        </p>
                                        {selectedDevices.length > 0 && (
                                            <button
                                                onClick={() => setSelectedDevices([])}
                                                className="text-xs text-gray-500 hover:text-gray-700 underline"
                                            >
                                                {t('common.clearSelection', 'Clear selection')}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Device List */}
                                <div className="p-6 overflow-y-auto flex-1">
                                    {devicesLoading ? (
                                        <div className="flex flex-col items-center justify-center py-12">
                                            <div className="animate-spin rounded-full h-10 w-10 border-3 border-green-200 border-t-green-600"></div>
                                            <p className="mt-4 text-sm text-gray-500">{t('common.loading', 'Loading...')}</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {(() => {
                                                const filteredDevices = (devices || []).filter(device =>
                                                    device.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                                    device.device_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                                    device.id?.toLowerCase().includes(searchQuery.toLowerCase())
                                                );

                                                if (filteredDevices.length === 0) {
                                                    return (
                                                        <div className="text-center py-12">
                                                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                                <Search className="w-8 h-8 text-gray-400" />
                                                            </div>
                                                            <p className="text-gray-500">
                                                                {searchQuery
                                                                    ? t('deviceTags.noDevicesFound', 'No devices found matching your search')
                                                                    : t('deviceTags.noDevicesAvailable', 'No devices available')}
                                                            </p>
                                                        </div>
                                                    );
                                                }

                                                return filteredDevices.map((device) => {
                                                    const isSelected = selectedDevices.includes(device.id);
                                                    const isAlreadyAssigned = selectedTag.devices?.some(d => d.id === device.id);

                                                    return (
                                                        <label
                                                            key={device.id}
                                                            className={`
                                                                flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all border-2
                                                                ${isSelected
                                                                    ? 'bg-green-50 border-green-500 shadow-sm'
                                                                    : isAlreadyAssigned
                                                                        ? 'bg-gray-50 border-gray-200 opacity-60'
                                                                        : 'bg-white border-gray-200 hover:border-green-300 hover:shadow-sm'
                                                                }
                                                            `}
                                                        >
                                                            <div className="flex items-center space-x-3 flex-1">
                                                                <div className="relative">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        disabled={isAlreadyAssigned}
                                                                        onChange={(e) => {
                                                                            if (e.target.checked) {
                                                                                setSelectedDevices([...selectedDevices, device.id]);
                                                                            } else {
                                                                                setSelectedDevices(selectedDevices.filter(id => id !== device.id));
                                                                            }
                                                                        }}
                                                                        className="w-5 h-5 rounded-md border-2 border-gray-300 text-green-600 focus:ring-2 focus:ring-green-500 focus:ring-offset-0 disabled:opacity-50 cursor-pointer"
                                                                    />
                                                                    {isSelected && (
                                                                        <Check className="absolute inset-0 w-5 h-5 text-green-600 pointer-events-none" />
                                                                    )}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-medium text-gray-900 truncate">{device.name}</span>
                                                                        {isAlreadyAssigned && (
                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                                {t('deviceTags.alreadyAssigned', 'Assigned')}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex items-center gap-2 mt-1">
                                                                        <span className="text-sm text-gray-500">{device.device_type}</span>
                                                                        <span className="text-xs text-gray-400">•</span>
                                                                        <span className="text-xs text-gray-400 font-mono">{device.id}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </label>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    )}
                                </div>

                                {/* Modal Footer */}
                                <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
                                    <button
                                        onClick={() => {
                                            setShowAssignModal(false);
                                            setSelectedDevices([]);
                                            setSelectedTag(null);
                                            setSearchQuery('');
                                        }}
                                        className="px-5 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-medium shadow-sm"
                                    >
                                        {t('common.cancel')}
                                    </button>
                                    <button
                                        onClick={handleAssignTag}
                                        disabled={selectedDevices.length === 0 || assignTagMutation.isLoading}
                                        className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg font-medium flex items-center gap-2"
                                    >
                                        {assignTagMutation.isLoading ? (
                                            <>
                                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                                                <span>{t('common.saving', 'Saving...')}</span>
                                            </>
                                        ) : (
                                            <>
                                                <Check className="w-4 h-4" />
                                                <span>
                                                    {t('deviceTags.assignSelected', `Assign to ${selectedDevices.length} device${selectedDevices.length !== 1 ? 's' : ''}`)}
                                                </span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )
                }
            </div >
        </div >
    );
};

export default DeviceTagsManager;